/**
 * DEFCAT P5 — severity-by-model prefill resolution.
 *
 * When the analyst picks a defect TYPE in the New-Defect drawer, the Severity
 * radios should prefill with that type's *default* severity for the **active
 * class model** (the DA's override, or the catalogue's default model).
 * Switching the active class model re-prefills.
 *
 * The catalogue read tolerates BOTH severity shapes (mirrors the BE's
 * `firstDefaultClassId`, see lyse-defect-catalogues.service.ts):
 *   • post-FIX-1 (JSONB): `defaultSeverityByModel = { class_model_id: class_id }`
 *   • pre-FIX-1 (live, bare UUID): `defaultSeverityByModel = null`, but the BE
 *     still surfaces the single representative class id on `entry.defectClassId`.
 *
 * These helpers are pure (no React) so the resolution is unit-tested in
 * isolation from the drawer.
 */
import type { DefectCatalogueEntryV2 } from "@aeriosiq/types";

/** Minimal class shape the prefill needs (subset of DefectClass). */
export interface ClassLike {
  defectClassId: string;
  classCategory: number;
  sortOrder: number;
}

/** Minimal class-model shape (subset of DefectClassModel / ClassModelListItem). */
export interface ClassModelLike {
  classModelId: string;
  classCount: number;
  classes: ClassLike[];
}

/** The two type-entry fields the resolution reads. */
export type TypeEntryLike = Pick<
  DefectCatalogueEntryV2,
  "defaultSeverityByModel" | "defectClassId"
>;

/**
 * Resolve the default defect-class id for a picked TYPE entry under a given
 * class model. Returns `null` when no default can be resolved.
 *
 * Resolution order:
 *   1. `defaultSeverityByModel[classModelId]` (exact per-model default, FIX-1)
 *   2. any other mapped default (the type has defaults but not for THIS model —
 *      still better than nothing)
 *   3. `entry.defectClassId` (pre-FIX-1 bare-UUID fallback)
 */
export function resolveDefaultClassId(
  typeEntry: TypeEntryLike | null | undefined,
  classModelId: string | null,
): string | null {
  if (!typeEntry) return null;
  const map = typeEntry.defaultSeverityByModel;
  if (map && typeof map === "object") {
    if (classModelId) {
      const exact = map[classModelId];
      if (typeof exact === "string" && exact.length > 0) return exact;
    }
    const vals = Object.values(map).filter(
      (v): v is string => typeof v === "string" && v.length > 0,
    );
    if (vals.length > 0) return vals[0] ?? null;
  }
  const bare = typeEntry.defectClassId;
  return typeof bare === "string" && bare.length > 0 ? bare : null;
}

/**
 * Map a default class id to its severity CATEGORY within a class model.
 * Returns `null` when the class id isn't part of the model (e.g. the default
 * was authored for a different model) — the caller then falls back to the
 * model's lowest grade.
 */
export function severityCategoryForClassId(
  model: ClassModelLike | null | undefined,
  classId: string | null,
): number | null {
  if (!model || !classId) return null;
  const hit = model.classes.find((c) => c.defectClassId === classId);
  return hit ? hit.classCategory : null;
}

/**
 * Full prefill: given the picked type entry + the effective class model,
 * return the severity category to seed.
 *   • `null` for the "None" model (classCount === 0) or a model with no grades.
 *   • the type's resolved default grade when it maps into the model.
 *   • the model's lowest-sortOrder grade as a fallback, so a graded model's
 *     Severity is never left empty.
 */
export function prefillSeverity(
  typeEntry: TypeEntryLike | null | undefined,
  model: ClassModelLike | null | undefined,
): number | null {
  if (!model || model.classCount === 0) return null;
  const grades = [...model.classes].sort((a, b) => a.sortOrder - b.sortOrder);
  const lowest = grades[0];
  if (!lowest) return null;
  const defaultClassId = resolveDefaultClassId(typeEntry, model.classModelId);
  const cat = severityCategoryForClassId(model, defaultClassId);
  return cat ?? lowest.classCategory;
}
